/**
 * Hook encapsulating form submission logic for the FinalDocuments page.
 * Handles clinical document updates, API submission calls, and normal
 * upload-then-submit flow for medical PA orders.
 *
 * @module finalDocuments/hooks/useFormSubmission
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { medicalPaSubmission } from "../../../api/postCall/medicalPaSubmission";
import { updateClinicalDocument as updateClinicalDocumentViaApi } from "../../../api/postCall/updateClinicalDocument";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import { DocumentPageMapping } from "../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrderStatus } from "../../../enums/medicalPaOrderStatus";
import {
  resetFileUploadState,
  uploadFileToFirebaseStorage,
} from "../../../redux/slice/fileUploadSlice";
import { AppDispatch } from "../../../redux/store/store";
import { logError } from "../../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { mergePdfs } from "../../pharmaQuestionaire/utils/pdfMerger";
import { blobToFile } from "../utils/documentHelper";
import { generatePathForFileUpload } from "../utils/generatePathForFileUpload";

/** Props for the useFormSubmission hook. */
interface UseFormSubmissionProps {
  /** Current `status.submission` value, sourced from the Postgres accumulator. */
  submissionStatus?: string;
  /** Route-param order ID used for API calls, Firestore writes, and navigation. */
  orderId: string | undefined;
  /** Patient ID from order demographics; required by the clinical-document write API. */
  patientId: string;
  /** Attachment files to merge and upload on submission. */
  documents: File[];
  /** Firebase Storage download URL set after a successful file upload. */
  downloadURL: string | null;
  /** Current upload progress percentage (0–100). */
  progress: number;
  /** Local clinical-document page mapping to persist on submit when dirty. */
  documentPageMapping: DocumentPageMapping[];
  /** True when the user appended documents and the mapping changed this session. */
  mappingDirty: boolean;
}

/**
 * Manages submission state and exposes callbacks for the final-documents flow.
 *
 * @param props - Submission status, documents, upload state, and routing identifiers
 * @returns Submission state flags, action callbacks, and dynamic button label
 */
export const useFormSubmission = ({
  submissionStatus,
  orderId,
  patientId,
  documents,
  downloadURL,
  progress,
  documentPageMapping,
  mappingDirty,
}: UseFormSubmissionProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const orgId = getOrgIdForFetchExternalWorklist();

  const uuid = useMemo(() => uuidv4(), []);

  /**
   * Posts the PA submission API call based on the order's current submission status.
   * Navigates to the worklist for in-progress orders and no-ops for already-completed ones.
   */
  const handleApiCall = useCallback(async () => {
    const submisionStatus = submissionStatus ?? "";
    if (submisionStatus === MedicalPaOrderStatus.IN_PROGRESS) {
      navigate(`/worklists/`);
    } else if (
      submisionStatus === MedicalPaOrderStatus.COMPLETED ||
      submisionStatus === MedicalPaOrderStatus.ALREADY_FILLED
    ) {
      return;
    } else {
      try {
        await medicalPaSubmission(orderId ?? "", orgId, undefined);
        toast.success(ToastMessages.FORM_SUBMISSION_SUCCESS);
      } catch (error) {
        toast.error(ToastMessages.FORM_SUBMISSION_FAILURE);
        console.error(error);
      }
    }
  }, [submissionStatus, orderId, orgId, navigate]);

  /**
   * Upserts the clinical document to Postgres via
   * `POST /frontend-query/upsert/clinical-document` (keyed by order_id +
   * document_type). Sends `document_type`, `patient_id`, `file_path`, and
   * `document_name`, plus `document_page_mapping` when the user added files.
   *
   * @param filePath - Storage path of the uploaded clinical document
   */
  const updateClinicalDocumentPathInPostgres = useCallback(
    async (filePath: string): Promise<void> => {
      await updateClinicalDocumentViaApi({
        order_id: orderId ?? "",
        org_id: orgId ?? "",
        data: {
          document_type: "clinical_attachment",
          patient_id: patientId,
          file_path: filePath,
          document_name: `${uuid}.pdf`,
          ...(mappingDirty
            ? { document_page_mapping: documentPageMapping }
            : {}),
        },
      });
    },
    [orderId, orgId, patientId, uuid, mappingDirty, documentPageMapping],
  );

  /**
   * Updates the clinical document in Firestore and Postgres, then triggers
   * the submission API. Writes the uploaded file path to both stores, includes
   * `document_page_mapping` on the Postgres upsert when it changed this
   * session, calls the submission API, resets the file-upload state, and
   * navigates to the submission-status page.
   */
  const updateClinicalDocument = useCallback(async () => {
    try {
      setIsSubmitting(true);
      const filePath = generatePathForFileUpload(orderId ?? "", uuid);
      await FirestoreService.updateDocument(
        "medical_pa_orders",
        orderId ?? "",
        {
          "clinical_document.file_path": filePath,
        },
      );
      await updateClinicalDocumentPathInPostgres(filePath);
      await handleApiCall();
      dispatch(resetFileUploadState());
      navigate(`/worklists/submission-status/${orderId}`);
    } catch (error) {
      logError(error as Error, "Error updating clinical document");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    uuid,
    handleApiCall,
    dispatch,
    navigate,
    orderId,
    updateClinicalDocumentPathInPostgres,
  ]);

  /**
   * Handles the standard submission path: merges all attachment PDFs, uploads the
   * result to Firebase Storage, and relies on the `downloadURL` effect to trigger
   * `updateClinicalDocument` once the upload completes.
   */
  const submitNormalFlow = useCallback(async () => {
    const faxPdfLink = location?.state?.faxPdfLink;
    const filePath = faxPdfLink
      ? faxPdfLink
      : generatePathForFileUpload(orderId ?? "", uuid);
    if (documents && documents.length > 0) {
      setIsSubmitting(true);
      const mergedDocInfo = await mergePdfs(documents);
      if (mergedDocInfo) {
        dispatch(
          uploadFileToFirebaseStorage(
            blobToFile(mergedDocInfo.mergedPdf, `${uuid}.pdf`),
            filePath,
          ),
        );
      }
    }
  }, [documents, dispatch, uuid, orderId, location?.state?.faxPdfLink]);

  const finalButtonText = useMemo(() => {
    if (isSubmitting) {
      return downloadURL
        ? "Submitting..."
        : `Uploading ${Math.round(progress)}%`;
    }

    return "Submit";
  }, [isSubmitting, downloadURL, progress]);

  return {
    isSubmitting,
    setIsSubmitting,
    updateClinicalDocument,
    submitNormalFlow,
    finalButtonText,
  };
};
